import type { IRainMessageDraft, IRainToolCallState } from './types';
import type { IRainThreadAdapter } from './scope';
import {
  useRainChatAttachments,
  type IUseRainChatAttachmentsReturn,
} from './useRainChatAttachments';
import {
  useRainArtifactPreview,
  type IUseRainArtifactPreviewReturn,
} from './useRainArtifactPreview';
import {
  useRainThreadMessages,
  type IUseRainThreadMessagesReturn,
} from './useRainThreadMessages';

export interface IUseRainChatParams {
  /** Scope- and agent-bound API surface for the thread. */
  adapter: IRainThreadAdapter;
  /** Undefined while the thread is being created. */
  threadId: string | undefined;
  /** Drawn immediately, before a thread exists. See IUseRainThreadMessagesParams. */
  pendingFirstMessage?: IRainMessageDraft | null;
  /** Scope-specific side effect once a tool commits (any visibility, thread-wide). */
  onToolSucceeded?: (toolName: string) => void;
  /** Surface reaction to a surfaced root tool's own result. */
  onVisibleToolResult?: (state: IRainToolCallState) => void;
  /** See IUseRainChatAttachmentsParams. */
  deferUploadsWithoutThread?: boolean;
}

export interface IUseRainChatReturn {
  chat: IUseRainThreadMessagesReturn;
  attachments: IUseRainChatAttachmentsReturn;
  preview: IUseRainArtifactPreviewReturn;
}

/**
 * Everything a Rain chat surface needs: the message stream, the composer's
 * attachments, and the artifact preview, wired to each other.
 *
 * Every surface was assembling this same quartet by hand, differing only in the
 * adapter it passed. `RainChat` renders the result; this hook exists for the
 * surface that needs the bundle without the default layout — the run page mounts
 * it once and shares it across two views so they drive a single SSE stream.
 */
export function useRainChat({
  adapter,
  threadId,
  pendingFirstMessage,
  onToolSucceeded,
  onVisibleToolResult,
  deferUploadsWithoutThread,
}: IUseRainChatParams): IUseRainChatReturn {
  const preview = useRainArtifactPreview(adapter);
  const chat = useRainThreadMessages({
    rainThreadId: threadId,
    pendingFirstMessage,
    adapter,
    onAttachmentClick: preview.handleAttachmentClick,
    onToolSucceeded,
    onVisibleToolResult,
  });
  const attachments = useRainChatAttachments({
    agentId: adapter.agentId,
    threadId,
    scope: adapter.attachmentScope,
    deferUploadsWithoutThread,
  });

  return { chat, attachments, preview };
}
